import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { uploadDocument } from '@/api/documents'
import { Upload, FileText, Loader2, Check } from 'lucide-react'
import { toast } from 'sonner'

export function DocumentUpload() {
  const [file, setFile] = useState<File | null>(null)
  const [category, setCategory] = useState('implementation')
  const [productArea, setProductArea] = useState('')
  const queryClient = useQueryClient()

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected')
      return uploadDocument(file, category, productArea || undefined)
    },
    onSuccess: () => {
      toast.success('Document uploaded successfully')
      setFile(null)
      queryClient.invalidateQueries({ queryKey: ['implementation-documents'] })
    },
    onError: (error) => {
      toast.error(`Failed to upload: ${error.message}`)
    }
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Document</CardTitle>
          <CardDescription>
            Upload documents to be indexed and made available to partners
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Upload */}
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
            <input
              type="file"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              accept=".pdf,.docx,.txt,.md"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {file ? file.name : 'Click to select a file'}
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF, DOCX, TXT, or MD (max 10MB)
                </p>
              </div>
            </label>
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium">Category</label>
            <select 
              value={category} 
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2"
            >
              <option value="implementation">Implementation</option>
              <option value="mssp">MSSP</option>
              <option value="asd">ASD</option>
              <option value="general">General</option>
            </select>
          </div>

          {/* Product Area */}
          <div>
            <label className="text-sm font-medium">Product Area (optional)</label>
            <select 
              value={productArea} 
              onChange={(e) => setProductArea(e.target.value)}
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2"
            >
              <option value="">Select area...</option>
              <option value="SASE">SASE</option>
              <option value="Network Services">Network Services</option>
              <option value="Application Security">Application Security</option>
              <option value="Zero Trust">Zero Trust</option>
            </select>
          </div>

          <Button 
            onClick={() => uploadMutation.mutate()}
            disabled={!file || uploadMutation.isPending}
            className="w-full"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload Document
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Upload Instructions */}
      <Card className="bg-muted/50">
        <CardContent className="p-6">
          <h3 className="font-medium mb-2">Document Processing</h3>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>Documents are automatically indexed for AI search</li>
            <li>Text is extracted and chunked for optimal retrieval</li>
            <li>Processing may take a few minutes for large files</li>
            <li>Documents are stored securely in Cloudflare R2</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
